import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Plus, Wrench, Pencil, Trash2, X, Camera, ImageOff, ScanLine, Search } from "lucide-react";
import { toast } from "sonner";

const RESERVED_SHELVES = Array.from({ length: 10 }, (_, i) => `AX-${i + 2}`);
const MAX_UPLOAD_MB = 2;

/**
 * Registry for non-glass items sitting on AX-2 … AX-11. Grouped by shelf so
 * operators can walk to the sehpa and see exactly what's there.
 */
export default function MachineParts() {
  const { user } = useAuth();
  const isAdmin = ["admin", "supervisor"].includes(user?.role);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null); // null | {new:true, sehpa} | full item
  const [query, setQuery] = useState(""); // live filter text (name/kod)
  const [highlightId, setHighlightId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api
      .get("/machine-parts")
      .then(({ data }) => {
        setItems(data.items || []);
        setError("");
      })
      .catch((e) => setError(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const grouped = useMemo(() => {
    const map = Object.fromEntries(RESERVED_SHELVES.map((s) => [s, []]));
    const q = query.trim().toUpperCase();
    for (const it of items) {
      if (!map[it.sehpa]) continue;
      if (q) {
        const hay = `${it.name} ${it.kod || ""} ${it.description || ""}`.toUpperCase();
        if (!hay.includes(q)) continue;
      }
      map[it.sehpa].push(it);
    }
    return map;
  }, [items, query]);

  const totals = useMemo(() => {
    let parts = 0;
    let adet = 0;
    for (const it of items) {
      parts += 1;
      adet += it.adet || 0;
    }
    return { parts, adet };
  }, [items]);

  const handleDelete = async (id) => {
    if (!window.confirm("Bu parçayı silmek istediğine emin misin?")) return;
    try {
      await api.delete(`/machine-parts/${id}`);
      toast.success("Parça silindi");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  /**
   * Enter'a basılınca (USB tarayıcı veya elle) backend'de kod lookup yapar.
   * Tam eşleşme bulunursa: shelf-card'a scroll + satırı 3 sn boyunca vurgula
   * + toast'ta "AX-x rafında" bilgisi. Bulunmazsa hata toast.
   */
  const scanCode = async (raw) => {
    const code = String(raw || "").trim();
    if (!code) return { ok: false };
    try {
      const { data } = await api.get(`/machine-parts/by-code/${encodeURIComponent(code)}`);
      toast.success(`${data.name}`, {
        description: `${data.sehpa} rafında · ${data.adet} adet`,
        duration: 5000,
      });
      setHighlightId(data.id);
      setQuery("");
      // Wait a tick so the row renders unfiltered, then scroll + auto-clear
      setTimeout(() => {
        const el = document.querySelector(`[data-testid="part-row-${data.id}"]`);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 50);
      setTimeout(() => setHighlightId(null), 3500);
      return { ok: true };
    } catch (e) {
      toast.error(formatApiError(e));
      return { ok: false };
    }
  };

  return (
    <div className="space-y-6" data-testid="machine-parts-page">
      {/* Header */}
      <div className="hard border border-[color:var(--border)] bg-[color:var(--surface)] p-5 flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 hard bg-[color:var(--warning-bg)] text-[color:var(--warning)] flex items-center justify-center">
            <Wrench size={22} />
          </div>
          <div>
            <div className="ticker text-[10px] text-[color:var(--text-3)]">
              REZERVE SEHPALAR · AX-2 → AX-11
            </div>
            <h2 className="text-2xl font-black tracking-tight">
              Makina Parçaları
            </h2>
            <p className="text-sm text-[color:var(--text-2)] mt-1 max-w-2xl">
              Cam olmayan, sabit duran makina parçaları için hafif kayıt
              defteri. Fotoğraf + adet + kısa açıklama ile her sehpanın
              içeriği bir bakışta görünür.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <div className="text-right">
            <div className="ticker text-[10px] text-[color:var(--text-3)]">
              TOPLAM
            </div>
            <div className="text-3xl font-black tracking-tighter">
              {totals.parts}
              <span className="text-base font-bold text-[color:var(--text-3)] ml-2">
                parça · {totals.adet} adet
              </span>
            </div>
          </div>
          <button
            data-testid="machine-parts-add-btn"
            onClick={() => setEditing({ new: true, sehpa: RESERVED_SHELVES[0] })}
            className="inline-flex items-center gap-2 px-4 py-2.5 hard bg-[color:var(--primary)] text-white font-bold hover:bg-[color:var(--primary-2)] transition-colors"
          >
            <Plus size={16} strokeWidth={2.5} /> Parça Ekle
          </button>
        </div>
      </div>

      {error && (
        <div className="hard border border-[color:var(--error)] bg-[color:var(--error-bg)] text-[color:var(--error)] p-3">
          {error}
        </div>
      )}

      {/* Kod / isim arama — USB tarayıcı Enter'la buraya kod atar,
          servis anında rafı ve parçayı gösterir. */}
      <CodeScanBar
        query={query}
        onQueryChange={setQuery}
        onScan={scanCode}
      />

      {loading && (
        <div className="ticker text-[color:var(--text-3)]">Yükleniyor…</div>
      )}

      {/* Grid — one card per reserved shelf */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {RESERVED_SHELVES.map((sehpa) => (
          <ShelfCard
            key={sehpa}
            sehpa={sehpa}
            parts={grouped[sehpa]}
            onAdd={() => setEditing({ new: true, sehpa })}
            onEdit={(p) => setEditing(p)}
            onDelete={handleDelete}
            canDelete={isAdmin}
            highlightId={highlightId}
          />
        ))}
      </div>

      {editing && (
        <PartEditor
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}
    </div>
  );
}

/**
 * Auto-focusing barcode / code search bar. USB scanners fire Enter after the
 * code, so we call `onScan(value)` on Enter. Live typing filters the grid
 * below via `onQueryChange`; the input clears itself only after a successful
 * scan (so operators can keep typing to refine while browsing).
 */
function CodeScanBar({ query, onQueryChange, onScan }) {
  const ref = useRef(null);
  const flashTimer = useRef(null);

  useEffect(() => {
    ref.current?.focus();
  }, []);

  const flash = (kind) => {
    const el = ref.current;
    if (!el) return;
    el.classList.remove("scanner-flash-success", "scanner-flash-error");
    void el.offsetWidth;
    el.classList.add(kind === "ok" ? "scanner-flash-success" : "scanner-flash-error");
    clearTimeout(flashTimer.current);
    flashTimer.current = setTimeout(() => {
      el.classList.remove("scanner-flash-success", "scanner-flash-error");
    }, 500);
  };

  const submit = async () => {
    const val = ref.current?.value?.trim();
    if (!val) return;
    const res = await onScan?.(val);
    if (res && res.ok) {
      flash("ok");
      ref.current.value = "";
      onQueryChange("");
    } else {
      flash("error");
    }
    ref.current?.focus();
  };

  return (
    <div
      data-testid="machine-parts-scan-bar"
      className="hard border border-[color:var(--border)] bg-[color:var(--surface)] p-3 flex items-center gap-2"
    >
      <ScanLine className="text-[color:var(--accent)] flex-shrink-0" size={22} />
      <input
        ref={ref}
        type="text"
        data-testid="machine-parts-scan-input"
        value={query}
        onChange={(e) => onQueryChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            submit();
          }
        }}
        placeholder="Kod okutun (USB tarayıcı / elle) veya isimle filtrele — Enter arar"
        autoComplete="off"
        spellCheck={false}
        className="scanner-input flex-1 hard border border-dashed border-[color:var(--border)] bg-white px-3 py-2 text-base font-mono focus:outline-none focus:border-[color:var(--accent)] focus:bg-[color:var(--accent-bg)]"
      />
      <button
        data-testid="machine-parts-scan-btn"
        onClick={submit}
        className="inline-flex items-center gap-2 px-3 py-2 hard bg-[color:var(--primary)] text-white text-sm font-bold hover:bg-[color:var(--primary-2)]"
        title="Enter'a basmakla aynı"
      >
        <Search size={14} /> Ara
      </button>
      {query && (
        <button
          onClick={() => {
            onQueryChange("");
            ref.current?.focus();
          }}
          className="p-2 hard border border-[color:var(--border)] hover:border-[color:var(--error)] hover:text-[color:var(--error)]"
          title="Filtreyi temizle"
        >
          <X size={14} />
        </button>
      )}
    </div>
  );
}


function ShelfCard({ sehpa, parts, onAdd, onEdit, onDelete, canDelete, highlightId }) {
  const adet = parts.reduce((a, b) => a + (b.adet || 0), 0);
  return (
    <div
      data-testid={`shelf-card-${sehpa}`}
      className="hard border border-[color:var(--border)] bg-[color:var(--surface)] flex flex-col"
    >
      <div className="p-3 border-b border-[color:var(--border)] flex items-center justify-between bg-[color:var(--warning-bg)]">
        <div className="flex items-center gap-2">
          <Wrench size={14} className="text-[color:var(--warning)]" />
          <span className="font-mono font-black tracking-widest">{sehpa}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="ticker text-[10px] text-[color:var(--text-3)]">
            {parts.length} parça · {adet} adet
          </span>
          <button
            data-testid={`shelf-add-${sehpa}`}
            onClick={onAdd}
            title="Bu sehpaya parça ekle"
            className="p-1.5 hard border border-[color:var(--border)] hover:border-[color:var(--primary)] hover:text-[color:var(--primary)] transition-colors bg-[color:var(--surface)]"
          >
            <Plus size={13} strokeWidth={2.5} />
          </button>
        </div>
      </div>

      <div className="p-3 flex-1 min-h-[120px]">
        {parts.length === 0 ? (
          <div className="text-xs text-[color:var(--text-3)] italic h-full flex items-center justify-center py-6 text-center">
            Bu sehpada henüz kayıtlı parça yok.
          </div>
        ) : (
          <ul className="space-y-2">
            {parts.map((p) => (
              <li
                key={p.id}
                data-testid={`part-row-${p.id}`}
                className={`flex items-center gap-3 p-2 hard border transition-colors ${
                  highlightId === p.id
                    ? "border-[color:var(--accent)] bg-[color:var(--accent-bg)] ring-2 ring-[color:var(--accent)]"
                    : "border-[color:var(--border)] hover:border-[color:var(--primary)]"
                }`}
              >
                <PartThumb photo={p.photo} name={p.name} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-bold text-sm truncate">{p.name}</div>
                    {p.kod && (
                      <span
                        data-testid={`part-kod-${p.id}`}
                        className="inline-flex items-center px-1.5 py-0.5 hard text-[10px] font-mono font-bold bg-[color:var(--primary)] text-white tracking-wider"
                      >
                        {p.kod}
                      </span>
                    )}
                  </div>
                  {p.description && (
                    <div className="text-xs text-[color:var(--text-3)] truncate">
                      {p.description}
                    </div>
                  )}
                </div>
                <div className="text-right">
                  <div className="font-mono font-black text-lg leading-none">
                    {p.adet}
                  </div>
                  <div className="ticker text-[9px] text-[color:var(--text-3)]">
                    ADET
                  </div>
                </div>
                <div className="flex flex-col gap-1">
                  <button
                    data-testid={`part-edit-${p.id}`}
                    onClick={() => onEdit(p)}
                    title="Düzenle"
                    className="p-1 hard border border-[color:var(--border)] hover:border-[color:var(--primary)] hover:text-[color:var(--primary)]"
                  >
                    <Pencil size={12} />
                  </button>
                  {canDelete && (
                    <button
                      data-testid={`part-delete-${p.id}`}
                      onClick={() => onDelete(p.id)}
                      title="Sil (admin)"
                      className="p-1 hard border border-[color:var(--border)] hover:border-[color:var(--error)] hover:text-[color:var(--error)]"
                    >
                      <Trash2 size={12} />
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function PartThumb({ photo, name }) {
  if (!photo) {
    return (
      <div
        title={`${name} — foto yok`}
        className="w-12 h-12 hard border border-dashed border-[color:var(--border)] flex items-center justify-center text-[color:var(--text-3)] bg-[color:var(--bg)] flex-shrink-0"
      >
        <ImageOff size={16} />
      </div>
    );
  }
  return (
    <img
      src={photo}
      alt={name}
      className="w-12 h-12 hard object-cover border border-[color:var(--border)] flex-shrink-0"
    />
  );
}

/**
 * Modal editor. `initial` is either `{new:true, sehpa}` for create or the full
 * part object for edit. Photos are downscaled client-side to ~800px on the
 * long edge before base64 upload so we never hit the 2 MB backend cap for
 * typical phone snaps.
 */
function PartEditor({ initial, onClose, onSaved }) {
  const isNew = !!initial?.new;
  const [sehpa, setSehpa] = useState(initial?.sehpa || RESERVED_SHELVES[0]);
  const [name, setName] = useState(initial?.name || "");
  const [kod, setKod] = useState(initial?.kod || "");
  const [description, setDescription] = useState(initial?.description || "");
  const [adet, setAdet] = useState(initial?.adet ?? 1);
  const [photo, setPhoto] = useState(initial?.photo || null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > MAX_UPLOAD_MB * 1024 * 1024 * 2) {
      toast.error(`Foto çok büyük (>${MAX_UPLOAD_MB * 2} MB)`);
      return;
    }
    try {
      const dataUrl = await downscaleImage(file, 800);
      setPhoto(dataUrl);
    } catch (e) {
      toast.error("Foto işlenemedi: " + (e?.message || e));
    }
  };

  const save = async () => {
    if (!name.trim()) {
      toast.error("Parça adı gerekli");
      return;
    }
    setSaving(true);
    try {
      const body = {
        sehpa,
        name: name.trim(),
        kod: kod.trim(),
        description: description.trim(),
        adet: Number(adet) || 0,
        photo,
      };
      if (isNew) {
        await api.post("/machine-parts", body);
        toast.success("Parça eklendi");
      } else {
        await api.put(`/machine-parts/${initial.id}`, body);
        toast.success("Parça güncellendi");
      }
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="machine-part-editor"
    >
      <div
        className="hard bg-[color:var(--surface)] border border-[color:var(--border)] w-full max-w-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 border-b border-[color:var(--border)] flex items-center justify-between">
          <h3 className="font-black">
            {isNew ? "Yeni Parça" : "Parçayı Düzenle"}
          </h3>
          <button onClick={onClose} className="p-1 hover:text-[color:var(--error)]" data-testid="editor-close">
            <X size={18} />
          </button>
        </div>
        <div className="p-4 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="ticker text-[10px] text-[color:var(--text-3)]">
                SEHPA
              </span>
              <select
                data-testid="editor-sehpa"
                value={sehpa}
                onChange={(e) => setSehpa(e.target.value)}
                className="mt-1 w-full hard border border-[color:var(--border)] bg-[color:var(--bg)] px-2 py-2 font-mono font-bold"
              >
                {RESERVED_SHELVES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="ticker text-[10px] text-[color:var(--text-3)]">
                ADET
              </span>
              <input
                data-testid="editor-adet"
                type="number"
                min="0"
                value={adet}
                onChange={(e) => setAdet(e.target.value)}
                className="mt-1 w-full hard border border-[color:var(--border)] bg-[color:var(--bg)] px-2 py-2 font-mono"
              />
            </label>
          </div>

          <label className="block">
            <span className="ticker text-[10px] text-[color:var(--text-3)]">
              PARÇA ADI
            </span>
            <input
              data-testid="editor-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ör. Konveyör Rulman 6205"
              className="mt-1 w-full hard border border-[color:var(--border)] bg-[color:var(--bg)] px-2 py-2"
            />
          </label>

          <label className="block">
            <span className="ticker text-[10px] text-[color:var(--text-3)]">
              KOD / BARKOD (opsiyonel)
            </span>
            <input
              data-testid="editor-kod"
              value={kod}
              onChange={(e) => setKod(e.target.value)}
              placeholder="Ör. RUL-6205 veya barkod"
              className="mt-1 w-full hard border border-[color:var(--border)] bg-[color:var(--bg)] px-2 py-2 font-mono uppercase"
              autoComplete="off"
            />
          </label>

          <label className="block">
            <span className="ticker text-[10px] text-[color:var(--text-3)]">
              AÇIKLAMA
            </span>
            <textarea
              data-testid="editor-description"
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="İsteğe bağlı: kaynağı, ölçüsü, notu"
              className="mt-1 w-full hard border border-[color:var(--border)] bg-[color:var(--bg)] px-2 py-2 text-sm"
            />
          </label>

          <div>
            <span className="ticker text-[10px] text-[color:var(--text-3)]">
              FOTOĞRAF
            </span>
            <div className="mt-1 flex items-center gap-3">
              {photo ? (
                <img
                  src={photo}
                  alt="preview"
                  className="w-24 h-24 hard object-cover border border-[color:var(--border)]"
                />
              ) : (
                <div className="w-24 h-24 hard border border-dashed border-[color:var(--border)] flex items-center justify-center text-[color:var(--text-3)]">
                  <ImageOff size={20} />
                </div>
              )}
              <div className="flex flex-col gap-2">
                <input
                  ref={fileRef}
                  data-testid="editor-photo-input"
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                  onChange={(e) => handleFile(e.target.files?.[0])}
                />
                <button
                  data-testid="editor-photo-btn"
                  onClick={() => fileRef.current?.click()}
                  className="inline-flex items-center gap-2 px-3 py-1.5 hard border border-[color:var(--border)] hover:border-[color:var(--primary)] text-sm"
                >
                  <Camera size={14} /> {photo ? "Fotoğrafı Değiştir" : "Foto Ekle / Çek"}
                </button>
                {photo && (
                  <button
                    onClick={() => setPhoto(null)}
                    className="text-xs text-[color:var(--error)] hover:underline text-left"
                  >
                    Fotoğrafı kaldır
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="p-4 border-t border-[color:var(--border)] flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 hard border border-[color:var(--border)] text-sm hover:bg-[color:var(--bg)]"
          >
            Vazgeç
          </button>
          <button
            data-testid="editor-save"
            onClick={save}
            disabled={saving}
            className="px-4 py-2 hard bg-[color:var(--primary)] text-white font-bold text-sm hover:bg-[color:var(--primary-2)] disabled:opacity-50"
          >
            {saving ? "Kaydediliyor…" : "Kaydet"}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Downscale an image File to a JPEG data URL with the long edge <= maxDim.
 * Cheap client-side compression keeps documents small and avoids hitting the
 * 2 MB backend photo cap even from high-res phone cameras.
 */
async function downscaleImage(file, maxDim = 800) {
  const dataUrl = await new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(fr.result);
    fr.onerror = () => reject(new Error("Dosya okunamadı"));
    fr.readAsDataURL(file);
  });
  const img = await new Promise((resolve, reject) => {
    const el = new Image();
    el.onload = () => resolve(el);
    el.onerror = () => reject(new Error("Görsel yüklenemedi"));
    el.src = dataUrl;
  });
  let { width, height } = img;
  const scale = Math.min(1, maxDim / Math.max(width, height));
  width = Math.round(width * scale);
  height = Math.round(height * scale);
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0, width, height);
  return canvas.toDataURL("image/jpeg", 0.82);
}
